<script setup lang="ts">
    import _ from 'lodash';
    import { computed, onMounted, reactive, ref, watch } from 'vue';
    import {
        deleteAttributeDefinitions,
        getAttributeDefinitions,
        getCode,
        getCodeSets,
        getLibraries,
        putAttributeDefinitions,
        validateReferenceFilter
    } from '@/shared/helpers/api';
    import {
        attributeTypes,
        attributeTypeToInputType,
        attributeTypeToStepSize,
        codeRefDisplayModes
    } from '@/shared/helpers/utils';
    import DateTimePicker from '@/shared/components/DateTimePicker.vue';
    import LibrarySelector from '@/shared/components/LibrarySelector.vue';
    import AddChoiceModal from '@/shared/components/AddChoiceModal.vue';
    import DynamicFilterBuilderModal from '@/shared/components/DynamicFilterBuilderModal.vue';
    import AttributeRenameModal from './AttributeRenameModal.vue';
    import { validateAttributeName } from './attributeNameValidation.ts';
    import SqlFilterEditControl from '@/shared/components/SqlFilterEditControl.vue';
    import { InputRegexValidator } from '@/shared/helpers/inputRegexValidator.ts';
    import debounce from 'lodash/debounce';
    import { validateRegex } from '@/shared/helpers/validateRegex.ts';
    import { AttributeDefinition, AttributeValueChoice, Library } from '@/shared/helpers/api/types/entities.ts';
    import { AttributeValue } from '@/shared/codeedit/codeEditFields.ts';
    import { ReferenceFilterNode } from '@/shared/codeedit/dynamicFilterTypes.ts';
    import { formatPredicateLines } from '@/shared/codeedit/dynamicFilterSummary.ts';
    import { ClauseContext, ruleMatchesCase } from '@/shared/codeedit/dynamicFilterClauses.ts';
    import { http } from '@/shared/httpWrapper';

    const props = defineProps<{
        attributedefs: AttributeDefinition[];
        library: Library;
    }>();

    const emit = defineEmits<{
        errorChanged: [value: boolean];
    }>();

    defineExpose({
        updateAttributes,
        newAD
    });

    const libraries = ref<Library[] | null>(null);
    const allLibraryNames = ref<string[] | null>(null);

    async function loadLibraries() {
        libraries.value = await getLibraries(http);
        allLibraryNames.value = libraries.value
            .filter(l =>
                !l.isVirtual
                && (l.isGlobal || !props.library.isGlobal && props.library.scopeType === l.scopeType))
            .map(l => l.name)
            .sort();
    }

    // Whether each library is case-sensitive, by lower-case name, for the dynamic filter builder and
    // summary. Already loaded with the libraries, so neither needs a request of its own.
    const libraryCaseSensitivity = computed(() =>
        new Map((libraries.value ?? []).map(l => [l.name.toLowerCase(), l.isCaseSensitive])));

    function isGlobal(libraryName: string) {
        return libraries.value?.find(l => l.name === libraryName)?.isGlobal ?? false;
    }

    const deleteAttrDefs = ref<string[]>([]);
    const editAttrDefs = ref<AttributeDefinition[]>(JSON.parse(JSON.stringify(props.attributedefs)));
    const newAttrDefs = ref<AttributeDefinition[]>([]);
    const openedDetailedAttrs = ref<AttributeDefinition[]>([]);

    // Pending attribute renames, keyed by the attribute's original (unchanged) name => new name.
    // The attribute definition object keeps its original name until save, so all name-keyed state
    // (validation results, regex errors, etc.) stays stable; only the displayed name and the save
    // payload use the new name.
    const renames = reactive<Record<string, string>>({});

    const attrDefs = computed(() => {
        return editAttrDefs.value.concat(newAttrDefs.value);
    });

    function isToBeDeletedAttr(name: string) {
        return deleteAttrDefs.value.some((attrName) => attrName === name);
    }

    function isExistingAttr(name: string) {
        return editAttrDefs.value.some((attr) => attr.name === name);
    }

    function isNewAttr(name: string) {
        return newAttrDefs.value.some((attr) => attr.name === name);
    }

    function newAD() {
        const maxSeqFromNew = newAttrDefs.value.length ? _.maxBy(newAttrDefs.value, 'sequenceNumber')?.sequenceNumber ?? 0 : 0;
        const maxSeqFromEdit = editAttrDefs.value.length ? _.maxBy(editAttrDefs.value, 'sequenceNumber')?.sequenceNumber ?? 0 : 0;
        const nextSeq = Math.max(maxSeqFromNew, maxSeqFromEdit) + 1;
        newAttrDefs.value.push(
            {
                name: 'Attribute' + nextSeq,
                description: '',
                regex: '',
                regexErrorMessage: '',
                displayAs: '',
                attributeType: 'String',
                sequenceNumber: nextSeq,
                required: false,
                includeInIdentity: false,
                defaultValue: '',
                choices: [],
                referenceDisplayMode: 0,
                referenceFilterStatic: '',
                referenceFilterStaticErrorMessage: '',
                referenceFilterDynamic: null,
                referenceFilterDynamicErrorMessage: '',
                referenceFilterDynamicShowFiltered: false,
                referenceLibraryName: '',
                textAreaLines: 0,
                isMarkdown: false,
                treatAsAttribute: false,
                treatAsAttributeLabel: null
            });
    }

    function removeAD(name: string) {
        if (isExistingAttr(name)) {
            if (deleteAttrDefs.value.includes(name)) {
                _.remove(deleteAttrDefs.value, (attrName) => attrName === name);
            } else {
                if (openedDetailedAttrs.value.some((attr) => attr.name === name))
                    _.remove(openedDetailedAttrs.value, (i) => i.name === name);
                // Drop any pending rename so we don't send a rename for a deleted attribute.
                delete renames[name];
                deleteAttrDefs.value.push(name);
            }
        } else {
            _.remove(newAttrDefs.value, (attr) => attr.name === name);
        }
    }

    const choiceModalTarget = ref<AttributeDefinition | null>(null);

    function addChoice(attributeDefinition: AttributeDefinition) {
        choiceModalTarget.value = attributeDefinition;
    }

    const validChoiceTypes = ['String', 'Int', 'Float', 'Date', 'DateTime', 'Uri'];

    function onChoiceConfirmed(v: string) {
        if (choiceModalTarget.value) {
            choiceModalTarget.value.choices.push({ value: v });
        }
        choiceModalTarget.value = null;
    }

    function onChoiceCancelled() {
        choiceModalTarget.value = null;
    }

    const openDynamicFilterAttribute = ref<AttributeDefinition | null>(null);

    const dynamicFilterParamOptions = computed(() => {
        if (!openDynamicFilterAttribute.value) return [];
        return attrDefs.value.filter(a => a.name !== openDynamicFilterAttribute.value?.name);
    });

    function openDynamicFilterBuilder(attr: AttributeDefinition) {
        openDynamicFilterAttribute.value = attr;
    }

    function onDynamicFilterConfirmed(predicateJson: ReferenceFilterNode) {
        if (openDynamicFilterAttribute.value) {
            openDynamicFilterAttribute.value.referenceFilterDynamic = predicateJson;
        }
        openDynamicFilterAttribute.value = null;
    }

    function onDynamicFilterCleared() {
        if (openDynamicFilterAttribute.value) {
            openDynamicFilterAttribute.value.referenceFilterDynamic = null;
        }
        openDynamicFilterAttribute.value = null;
    }

    function onDynamicFilterCancelled() {
        openDynamicFilterAttribute.value = null;
    }

    const maxSummaryLines = 3;

    function hasCodeRefRule(node: ReferenceFilterNode): boolean {
        return node.group === 'CodeRef' || (node.clauses ?? []).some(hasCodeRefRule);
    }

    // Attribute definitions of the libraries that dynamic filters with CodeRef rules point into, by
    // lower-case name. The summary uses them to tell which CodeRef rules match case.
    const referencedAttrDefs = ref<Record<string, AttributeDefinition[]>>({});
    const failedReferencedLibraries = ref<Record<string, boolean>>({});
    const requestedReferencedLibraries = new Set<string>();

    watch(
        () => attrDefs.value
            .filter(a => a.referenceFilterDynamic && hasCodeRefRule(a.referenceFilterDynamic))
            .map(a => (a.referenceLibraryName ?? '').toLowerCase())
            .filter(name => name !== ''),
        async names => {
            for (const name of new Set(names)) {
                if (requestedReferencedLibraries.has(name)) continue;
                requestedReferencedLibraries.add(name);
                try {
                    referencedAttrDefs.value[name] = await getAttributeDefinitions(http, name);
                } catch {
                    failedReferencedLibraries.value[name] = true;
                }
            }
        },
        { immediate: true });

    // Shown under the summary when it cannot tell which of the filter's CodeRef rules match case
    function caseSensitivityUnknownMessage(attr: AttributeDefinition): string | null {
        const referenceLibraryName = attr.referenceLibraryName ?? '';
        if (!failedReferencedLibraries.value[referenceLibraryName.toLowerCase()]) return null;
        return `Could not load the columns of ${referenceLibraryName}, so CodeRef rules that match case are not marked.`;
    }

    function dynamicFilterSummaryLines(attr: AttributeDefinition): string[] {
        const referenceLibraryName = attr.referenceLibraryName ?? '';
        const ctx: ClauseContext = {
            currentLibraryName: props.library.name,
            currentLibraryAttrDefs: attrDefs.value,
            refLibAttrDefs: referencedAttrDefs.value[referenceLibraryName.toLowerCase()] ?? [],
            libraryCaseSensitivity: libraryCaseSensitivity.value
        };
        return formatPredicateLines(
            attr.referenceFilterDynamic ?? null,
            referenceLibraryName,
            props.library.name,
            node => ruleMatchesCase(node, ctx));
    }

    function shownDynamicFilterLines(attr: AttributeDefinition): string[] {
        return dynamicFilterSummaryLines(attr).slice(0, maxSummaryLines);
    }

    function hiddenDynamicFilterLineCount(attr: AttributeDefinition): number {
        return Math.max(dynamicFilterSummaryLines(attr).length - maxSummaryLines, 0);
    }

    function deleteChoice(attributeDefinition: AttributeDefinition, choice: AttributeValueChoice) {
        attributeDefinition.choices.splice(attributeDefinition.choices.indexOf(choice), 1);
    }

    const renameModalTarget = ref<AttributeDefinition | null>(null);

    function isRenamed(attr: AttributeDefinition) {
        return renames[attr.name] !== undefined;
    }

    function displayName(attr: AttributeDefinition) {
        return renames[attr.name] ?? attr.name;
    }

    const otherAttributeNames = computed(() =>
        renameModalTarget.value
            ? attrDefs.value.filter(a => a !== renameModalTarget.value).map(displayName)
            : []);

    function openRename(attr: AttributeDefinition) {
        renameModalTarget.value = attr;
    }

    function onRenameConfirmed(newName: string) {
        const target = renameModalTarget.value;
        if (target) {
            // target.name stays the original name, so comparing against it detects a revert.
            if (newName === target.name) {
                delete renames[target.name];
            } else {
                renames[target.name] = newName;
            }
        }
        renameModalTarget.value = null;
    }

    function onRenameCancelled() {
        renameModalTarget.value = null;
    }

    function revertRename(attr: AttributeDefinition) {
        delete renames[attr.name];
    }

    function resetRenameState() {
        Object.keys(renames).forEach(key => delete renames[key]);
    }

    // Creating and renaming an attribute enforce the same rules (regex, reserved names, in-library
    // uniqueness) via the shared helper. Existing-codes checks stay on the backend.
    const attributeNameValidationErrors = computed(() => {
        const errors: Record<string, string | null> = {};
        newAttrDefs.value.forEach(attr => {
            const otherNames = attrDefs.value
                .filter(a => a !== attr)
                .map(displayName);
            errors[attr.name] = validateAttributeName(attr.name, otherNames);
        });
        return errors;
    });

    const regexErrors = reactive<Record<string, string | null>>({});

    function regexErrorMessage(attribute: AttributeDefinition): string {
        if (!attribute.regex) {
            return '';
        } else if (regexErrors[attribute.name]) {
            return regexErrors[attribute.name] ?? '';
        } else {
            const validator = new RegExp(attribute.regex);
            const missingChoices = attribute.choices.filter(c => !validator.test(c.value?.toString() ?? ''));
            if (missingChoices.length > 0) {
                return `${missingChoices.length} choice(s) not included in regex, including ${missingChoices[0].value}`;
            } else {
                return '';
            }
        }
    }

    function isRegexErrorMessageWithoutRegex(regex: string | null, message: string | null) {
        return message && !regex;
    }

    function makeValidator(regex: string | null, message: string | null) {
        return regex ? new InputRegexValidator(new RegExp(regex), message) : null;
    }

    function invalidInputError(attribute: AttributeDefinition) {
        if (regexErrors[attribute.name]) {
            return null;
        } else {
            const errorMessage = makeValidator(attribute.regex, attribute.regexErrorMessage)?.validate(attribute.defaultValue);
            return errorMessage?.error;
        }
    }

    function showMaxLength(attribute: AttributeDefinition) {
        return invalidInputError(attribute)
            ? makeValidator(attribute.regex, attribute.regexErrorMessage)?.maxLength
            : undefined;
    }

    const lastCheckedCodeRefValues = reactive<Record<string, AttributeValue>>({});
    const codeRefValidationResults = reactive<Record<string, { type: string; message: string; isValid: boolean } | null>>({});

    const codeRefsToValidate = computed<{ name: string; referenceLibraryName: string; defaultValue: AttributeValue }[] | undefined>(() => {
        if (!libraries.value) return;
        return attrDefs.value
            .filter(attr =>
                isCodeRefType(attr.attributeType)
                && attr.defaultValue
                && isGlobal(attr.referenceLibraryName ?? ''))
            .map(attr => {
                return {
                    name: attr.name,
                    referenceLibraryName: attr.referenceLibraryName ?? '',
                    defaultValue: attr.defaultValue
                };
            });
    });

    watch(codeRefsToValidate, debounce(async () => {
        const toCheck = codeRefsToValidate.value?.filter(
            attr => lastCheckedCodeRefValues[attr.name] !== attr.defaultValue
        ) ?? [];
        if (toCheck.length > 0) {
            await Promise.all(toCheck.map(attr => validateDefaultCodeRef(attr)));
        }
    }, 500));

    function onCodeRefDefaultValueInput(name: string, defaultValue: AttributeValue) {
        if (defaultValue) {
            codeRefValidationResults[name] = {
                type: 'is-info',
                message: 'Validating...',
                isValid: false
            };
        } else {
            codeRefValidationResults[name] = null;
        }
    }

    async function validateDefaultCodeRef(attr: { name: string; referenceLibraryName: string; defaultValue: AttributeValue }) {
        codeRefValidationResults[attr.name] = {
            type: 'is-info',
            message: 'Validating...',
            isValid: false
        };

        const codesets = await getCodeSets(http, attr.referenceLibraryName);
        try {
            await getCode(http, codesets[0].name, attr.defaultValue?.toString() ?? '');
            codeRefValidationResults[attr.name] = {
                type: 'is-info',
                message: 'Valid!',
                isValid: true
            };
        } catch {
            codeRefValidationResults[attr.name] = {
                type: 'is-danger',
                message: 'No code found for this identity',
                isValid: false
            };
        }
        lastCheckedCodeRefValues[attr.name] = attr.defaultValue;
    }

    const lastCheckedFilters = reactive<Record<string, string>>({});
    const filterValidationResults = reactive<Record<string, { type: string; message: string; isValid: boolean } | null>>({});

    const codeRefFiltersToValidate = computed<{ name: string; referenceLibraryName: string; filter: string }[]>(() => {
        return attrDefs.value
            .filter(attr =>
                isCodeRefType(attr.attributeType)
                && !!attr.referenceLibraryName
                && !!attr.referenceFilterStatic?.trim())
            .map(attr => ({
                name: attr.name,
                referenceLibraryName: attr.referenceLibraryName ?? '',
                filter: attr.referenceFilterStatic ?? ''
            }));
    });

    watch(codeRefFiltersToValidate, debounce(async () => {
        const currentNames = new Set(codeRefFiltersToValidate.value.map(attr => attr.name));
        Object.keys(filterValidationResults)
            .filter(name => !currentNames.has(name))
            .forEach(name => {
                filterValidationResults[name] = null;
                delete lastCheckedFilters[name];
            });

        const toCheck = codeRefFiltersToValidate.value.filter(
            attr => lastCheckedFilters[attr.name] !== attr.filter
        );
        if (toCheck.length > 0) {
            await Promise.all(toCheck.map(attr => validateFilter(attr)));
        }
    }, 500));

    async function validateFilter(attr: { name: string; referenceLibraryName: string; filter: string }) {
        filterValidationResults[attr.name] = {
            type: 'is-info',
            message: 'Validating filter...',
            isValid: false
        };
        try {
            const { data } = await validateReferenceFilter(http, attr.referenceLibraryName, attr.filter);
            if (!data.isValid) {
                filterValidationResults[attr.name] = {
                    type: 'is-danger',
                    message: data.errorMessage ?? 'Invalid filter',
                    isValid: false
                };
            } else if (data.isEmptyResult) {
                filterValidationResults[attr.name] = {
                    type: 'is-warning',
                    message: 'Filter is valid but matches no items',
                    isValid: true
                };
            } else {
                filterValidationResults[attr.name] = {
                    type: 'is-success',
                    message: `Filter is valid and matches ${data.matchCount} ${data.matchCount === 1 ? 'item' : 'items'}`,
                    isValid: true
                };
            }
        } catch {
            filterValidationResults[attr.name] = {
                type: 'is-danger',
                message: 'Unable to validate filter',
                isValid: false
            };
        }
        lastCheckedFilters[attr.name] = attr.filter;
    }

    function codeRefPlaceholderText(attr: AttributeDefinition) {
        if (attr.referenceLibraryName && !isGlobal(attr.referenceLibraryName)) {
            return 'Cannot add default value to CodeRefs referencing scoped libraries';
        } else {
            return '';
        }
    }

    function handleAttributeTypeUpdated(attribute: AttributeDefinition) {
        attribute.defaultValue = '';
        attribute.choices = [];
        attribute.textAreaLines = 0;
        attribute.isMarkdown = false;
        codeRefValidationResults[attribute.name] = null;
        filterValidationResults[attribute.name] = null;
    }

    function handleCodeRefLibraryUpdated(attribute: AttributeDefinition) {
        attribute.defaultValue = '';
        codeRefValidationResults[attribute.name] = null;
        filterValidationResults[attribute.name] = null;
    }

    const hasErrors = computed(() => {
        return attrDefs.value.some(attr =>
            isRegexErrorMessageWithoutRegex(attr.regex, attr.regexErrorMessage)
            || attributeNameValidationErrors.value[attr.name]
            || regexErrors[attr.name]
            || (isStringType(attr.attributeType)
                && invalidInputError(attr))
            || (isCodeRefType(attr.attributeType)
                && !!codeRefValidationResults[attr.name]
                && !codeRefValidationResults[attr.name]!.isValid)
            || (isCodeRefType(attr.attributeType)
                && !!filterValidationResults[attr.name]
                && !filterValidationResults[attr.name]!.isValid)
            || attr.textAreaLines < 0
            || (attr.isMarkdown && attr.textAreaLines === 0));
    });

    watch(hasErrors, (newValue) => {
        emit('errorChanged', newValue);
    });

    async function updateAttributes() {
        try {
            await removeAttrDefinitions();
            await addAttrDefinitions();
            Object.entries(renames).forEach(([oldName, newName]) => {
                const attribute = editAttrDefs.value.find(attr => attr.name === oldName);
                if (attribute) attribute.name = newName;
            });
            resetRenameState();
        } catch (err: any) {
            const message = err.response.data.Message ?? err.response.data.errors;
            throw new Error(`Error in attribute definition input, cannot save: ${message}`, { cause: err });
        }
    }

    async function removeAttrDefinitions() {
        if (deleteAttrDefs.value.length > 0)
            return deleteAttributeDefinitions(http, props.library.name, { attributeDefinitions: deleteAttrDefs.value });
    }

    async function addAttrDefinitions() {
        let definitions: AttributeDefinition[] = [];

        if (newAttrDefs.value.length > 0)
            definitions = definitions.concat(newAttrDefs.value);

        editAttrDefs.value = editAttrDefs.value.map(editAttrDef => {
            if (editAttrDef.attributeType !== 'String' && editAttrDef.regex !== null) {
                editAttrDef.regex = null;
            }
            if (editAttrDef.attributeType !== 'String' && editAttrDef.regexErrorMessage !== null) {
                editAttrDef.regexErrorMessage = null;
            }
            if (editAttrDef.attributeType !== 'String') {
                editAttrDef.textAreaLines = 0;
                editAttrDef.isMarkdown = false;
            }
            if (editAttrDef.textAreaLines === 0) {
                editAttrDef.isMarkdown = false;
            }
            editAttrDef.treatAsAttributeLabel = editAttrDef.treatAsAttributeLabel?.trim() || null;
            return editAttrDef;
        });

        if (JSON.stringify(editAttrDefs.value) !== JSON.stringify(props.attributedefs))
            definitions = definitions.concat(editAttrDefs.value.filter(ed => !deleteAttrDefs.value.includes(ed.name)));

        if (definitions.length > 0 || Object.keys(renames).length > 0)
            return putAttributeDefinitions(http, props.library.name, definitions, renames);
    }

    function isReferenceType(attributeType: string) {
        return attributeType === 'CodeRef';
    }

    function isStringType(attributeType: string) {
        return attributeType === 'String';
    }

    function isDateType(attributeType: string) {
        return attributeType === 'Date'
            || attributeType === 'DateTime'
            || attributeType === 'DATE'
            || attributeType === 'DATETIME';
    }

    function isTimeType(attributeType: string) {
        return attributeType === 'DateTime' || attributeType === 'DATETIME';
    }

    function isCodeRefType(attributeType: string) {
        return attributeType === 'CodeRef' || attributeType === 'CODEREF';
    }

    const isRelationLibrary = computed(() => {
        return props.library.isRelation;
    });

    function isRelationCodeRef(row: AttributeDefinition) {
        return isRelationLibrary.value
            && isCodeRefType(row.attributeType)
            && row.includeInIdentity;
    }

    function onClickEnableMultiline(fieldDef: AttributeDefinition) {
        if (fieldDef.textAreaLines === 0) {
            fieldDef.textAreaLines = 1;
        } else {
            fieldDef.textAreaLines = 0;
            fieldDef.isMarkdown = false;
        }
    }

    onMounted(async () => {
        await loadLibraries();
    });
</script>

<template>
    <div>
        <b-table
            :data="attrDefs"
            hoverable
            detailed
            :opened-detailed="openedDetailedAttrs"
            :has-detailed-visible="(row: AttributeDefinition) => !isToBeDeletedAttr(row.name)"
            :row-class="(row: AttributeDefinition) => isToBeDeletedAttr(row.name) ? 'has-background-white-ter' : undefined">
            <b-table-column
                label="Name">
                <template #default="table">
                    <span
                        v-if="!isNewAttr(table.row.name)"
                        class="is-flex is-align-items-center pl-0-5">
                        <span>{{ displayName(table.row) }}</span>
                        <b-button
                            v-if="!isToBeDeletedAttr(table.row.name)"
                            class="ml-2"
                            type="is-text"
                            :icon-left="isRenamed(table.row) ? 'undo' : 'pencil'"
                            :title="isRenamed(table.row) ? 'Revert rename' : 'Rename attribute'"
                            @click="isRenamed(table.row) ? revertRename(table.row) : openRename(table.row)" />
                    </span>
                    <b-field
                        v-else
                        :type="attributeNameValidationErrors[table.row.name] !== null ? 'is-danger' : ''"
                        :message="attributeNameValidationErrors[table.row.name]">
                        <b-input v-model="table.row.name" />
                    </b-field>
                </template>
            </b-table-column>
            <b-table-column
                label="Seq#">
                <template #default="table">
                    <b-input
                        v-model="table.row.sequenceNumber"
                        :disabled="isToBeDeletedAttr(table.row.name) || isRelationCodeRef(table.row)"
                        :title="isRelationCodeRef(table.row) ? 'Cannot be changed for relation library' : undefined"
                        class="w-6"
                        type="number" />
                </template>
            </b-table-column>
            <b-table-column label="Display name">
                <template #default="table">
                    <b-input
                        v-model="table.row.displayAs"
                        :disabled="isToBeDeletedAttr(table.row.name)" />
                </template>
            </b-table-column>
            <b-table-column
                label="Type">
                <template #default="table">
                    <b-select
                        v-model="table.row.attributeType"
                        :disabled="isToBeDeletedAttr(table.row.name) || isRelationCodeRef(table.row)"
                        :title="isRelationCodeRef(table.row) ? 'Cannot be changed for relation library' : undefined"
                        @update:model-value="handleAttributeTypeUpdated(table.row)">
                        <option
                            v-for="attrType in attributeTypes"
                            :key="attrType">
                            {{ attrType }}
                        </option>
                    </b-select>
                </template>
            </b-table-column>
            <b-table-column label="CodeRef library">
                <template #default="table">
                    <b-select
                        v-model="table.row.referenceLibraryName"
                        :disabled="!isReferenceType(table.row.attributeType) || isToBeDeletedAttr(table.row.name) || isRelationCodeRef(table.row)"
                        :title="isRelationCodeRef(table.row) ? 'Cannot be changed for relation library' : undefined">
                        <option
                            v-for="libName in allLibraryNames"
                            :key="libName">
                            {{ libName }}
                        </option>
                    </b-select>
                </template>
            </b-table-column>
            <b-table-column label="Default Value">
                <template #default="table">
                    <span>{{ table.row.defaultValue }}</span>
                </template>
            </b-table-column>
            <b-table-column
                v-if="isRelationLibrary"
                label="Display in related library">
                <template #default="table">
                    <b-checkbox
                        v-if="isRelationCodeRef(table.row)"
                        v-model="table.row.treatAsAttribute"
                        :disabled="isToBeDeletedAttr(table.row.name)"
                        @update:model-value="(val: boolean) => { table.row.treatAsAttributeLabel = val ? (table.row.referenceLibraryName || null) : null; }" />
                </template>
            </b-table-column>
            <b-table-column
                v-if="isRelationLibrary"
                label="Label in related library">
                <template #default="table">
                    <b-field
                        v-if="isRelationCodeRef(table.row)"
                        :type="(table.row.treatAsAttribute && !table.row.treatAsAttributeLabel) ? 'is-danger' : undefined"
                        :message="(table.row.treatAsAttribute && !table.row.treatAsAttributeLabel) ? 'Label is required' : undefined">
                        <b-input
                            v-model="table.row.treatAsAttributeLabel"
                            :disabled="!table.row.treatAsAttribute || isToBeDeletedAttr(table.row.name)"
                            maxlength="256"
                            :has-counter="false" />
                    </b-field>
                </template>
            </b-table-column>
            <b-table-column label="In identity">
                <template #default="table">
                    <b-checkbox
                        v-model="table.row.includeInIdentity"
                        :disabled="isToBeDeletedAttr(table.row.name) || isRelationLibrary"
                        :title="isRelationCodeRef(table.row) ? 'CodeRefs for related libraries must be in identity' : (isRelationLibrary ? 'Relation library can only have two attributes in identity' : undefined)" />
                </template>
            </b-table-column>
            <b-table-column label="Required">
                <template #default="table">
                    <b-checkbox
                        v-model="table.row.required"
                        :disabled="isToBeDeletedAttr(table.row.name) || (isRelationLibrary && !isRelationCodeRef(table.row))"
                        :title="(isRelationLibrary && !isRelationCodeRef(table.row)) ? 'Only the two CodeRefs in identity can be required in relation libraries' : undefined" />
                </template>
            </b-table-column>
            <b-table-column label="Delete">
                <template #default="table">
                    <a
                        v-if="!isRelationCodeRef(table.row)"
                        @click="removeAD(table.row.name)">
                        <b-icon
                            v-if="!isToBeDeletedAttr(table.row.name)"
                            icon="trash-can" />
                        <b-icon
                            v-else
                            type="is-primary"
                            icon="trash-can" />
                    </a>
                    <span
                        v-else
                        class="has-text-grey-lighter not-allowed"
                        title="Cannot be deleted for relation library">
                        <b-icon
                            icon="trash-can" />
                    </span>
                </template>
            </b-table-column>
            <template #detail="table">
                <b-field
                    v-if="isNewAttr(table.row.name)"
                    horizontal
                    label="Name"
                    :type="attributeNameValidationErrors[table.row.name] ? 'is-danger' : ''"
                    :message="attributeNameValidationErrors[table.row.name]">
                    <b-input v-model="table.row.name" />
                </b-field>
                <b-field
                    label="Seq#"
                    horizontal>
                    <b-input
                        v-model="table.row.sequenceNumber"
                        :disabled="isRelationCodeRef(table.row)"
                        :title="isRelationCodeRef(table.row) ? 'Cannot be changed for relation library' : undefined"
                        type="number"
                        class="w-6" />
                </b-field>
                <b-field
                    label="Display name"
                    horizontal>
                    <b-input
                        v-model="table.row.displayAs" />
                </b-field>
                <b-field
                    label="Description"
                    horizontal>
                    <b-input
                        v-model="table.row.description"
                        type="textarea"
                        maxlength="2000" />
                </b-field>
                <b-field
                    horizontal
                    label="Type">
                    <b-select
                        v-model="table.row.attributeType"
                        :disabled="isRelationCodeRef(table.row)"
                        :title="isRelationCodeRef(table.row) ? 'Cannot be changed for relation library' : undefined"
                        expanded
                        class="w-20"
                        @update:model-value="handleAttributeTypeUpdated(table.row)">
                        <option
                            v-for="attrType in attributeTypes"
                            :key="attrType">
                            {{ attrType }}
                        </option>
                    </b-select>
                </b-field>
                <b-field
                    v-if="validChoiceTypes.includes(table.row.attributeType)"
                    label="Choices"
                    horizontal>
                    <div class="buttons">
                        <b-button
                            type="is-info"
                            icon-left="plus"
                            :disabled="isStringType(table.row.attributeType) && regexErrors[table.row.name]"
                            @click="() => addChoice(table.row)">
                            Add
                        </b-button>
                        <b-taglist>
                            <b-tag
                                v-for="c in table.row.choices"
                                :key="c.value"
                                type="is-success"
                                size="is-medium"
                                attached
                                closable
                                ellipsis
                                @close="() => deleteChoice(table.row, c)">
                                {{ c.value }}
                            </b-tag>
                        </b-taglist>
                    </div>
                </b-field>
                <b-field
                    horizontal
                    label="Default value"
                    :type="codeRefValidationResults[table.row.name]?.type"
                    :message="codeRefValidationResults[table.row.name]?.message">
                    <date-time-picker
                        v-if="isDateType(table.row.attributeType)"
                        v-model="table.row.defaultValue"
                        :input="table.row.defaultValue || ''"
                        :date-only="!isTimeType(table.row.attributeType)"
                        @dt-picker="(payload: string) => table.row.defaultValue = payload" />
                    <b-input
                        v-else-if="table.row.attributeType === 'Int' || table.row.attributeType === 'Float'"
                        v-model="table.row.defaultValue"
                        class="w-20"
                        :type="attributeTypeToInputType(table.row.attributeType )"
                        :step="attributeTypeToStepSize(table.row.attributeType )" />
                    <library-selector
                        v-else-if="['LibraryRef', 'LIBRARYREF'].includes(table.row.attributeType)"
                        class="w-20"
                        :selected="table.row.defaultValue"
                        @lib-selected="(payload: string) => table.row.defaultValue = payload" />
                    <b-input
                        v-else-if="isCodeRefType(table.row.attributeType)"
                        v-model="table.row.defaultValue"
                        :disabled="!isGlobal(table.row.referenceLibraryName)"
                        :class="codeRefPlaceholderText(table.row) ? 'w-40' : ''"
                        :placeholder="codeRefPlaceholderText(table.row)"
                        @input="onCodeRefDefaultValueInput(table.row.name, table.row.defaultValue)" />
                    <b-select
                        v-else-if="['Bool', 'BOOL'].includes(table.row.attributeType)"
                        v-model="table.row.defaultValue"
                        class="w-20"
                        expanded>
                        <option />
                        <option value="True">
                            True
                        </option>
                        <option value="False">
                            False
                        </option>
                    </b-select>
                    <b-field
                        v-else-if="table.row.attributeType === 'String' || table.row.attributeType === 'STRING'"
                        :type="invalidInputError(table.row) ? 'is-danger' : undefined"
                        :message="invalidInputError(table.row)">
                        <b-input
                            v-model="table.row.defaultValue"
                            :maxlength="showMaxLength(table.row)"
                            :type="attributeTypeToInputType(table.row.attributeType)" />
                    </b-field>
                    <b-input
                        v-else
                        v-model="table.row.defaultValue"
                        expanded />
                </b-field>
                <div
                    v-if="isReferenceType(table.row.attributeType)">
                    <b-field
                        label="CodeRef library"
                        horizontal
                        custom-class="wider-label">
                        <b-select
                            v-model="table.row.referenceLibraryName"
                            :disabled="isRelationCodeRef(table.row)"
                            :title="isRelationCodeRef(table.row) ? 'Cannot be changed for relation library' : undefined"
                            expanded
                            class="w-20"
                            @update:model-value="handleCodeRefLibraryUpdated(table.row)">
                            <option
                                v-for="libName in allLibraryNames"
                                :key="libName">
                                {{ libName }}
                            </option>
                        </b-select>
                    </b-field>
                    <b-field
                        label="CodeRef display"
                        horizontal>
                        <b-select
                            v-model="table.row.referenceDisplayMode"
                            expanded
                            class="w-20"
                            :disabled="!isReferenceType(table.row.attributeType)">
                            <option
                                v-for="o in codeRefDisplayModes"
                                :key="o.value"
                                :value="o.value">
                                {{ o.label }}
                            </option>
                        </b-select>
                    </b-field>
                    <b-field
                        label="CodeRef static filter"
                        horizontal
                        :type="filterValidationResults[table.row.name]?.type"
                        :message="filterValidationResults[table.row.name]?.message">
                        <sql-filter-edit-control
                            v-model="table.row.referenceFilterStatic"
                            :reference-library-name="table.row.referenceLibraryName" />
                    </b-field>
                    <b-field
                        label="CodeRef static filter hint"
                        horizontal>
                        <b-input
                            v-model="table.row.referenceFilterStaticErrorMessage"
                            maxlength="256" />
                    </b-field>
                    <b-field
                        label="Dynamic filter"
                        horizontal>
                        <div class="is-flex is-align-items-center is-gap-2">
                            <div
                                v-if="table.row.referenceFilterDynamic"
                                class="py-2 px-3 has-radius-normal has-background-success-95 has-text-success-on-scheme">
                                <div
                                    v-for="(line, lineIdx) in shownDynamicFilterLines(table.row)"
                                    :key="lineIdx">
                                    {{ line }}
                                </div>
                                <div v-if="hiddenDynamicFilterLineCount(table.row) > 0">
                                    +{{ hiddenDynamicFilterLineCount(table.row) }} more
                                </div>
                                <div
                                    v-if="caseSensitivityUnknownMessage(table.row)"
                                    class="is-size-7 has-text-danger">
                                    {{ caseSensitivityUnknownMessage(table.row) }}
                                </div>
                            </div>
                            <b-button
                                type="is-info"
                                icon-left="filter"
                                @click="openDynamicFilterBuilder(table.row)">
                                {{ table.row.referenceFilterDynamic ? 'Edit' : 'Add' }} dynamic filter
                            </b-button>
                        </div>
                    </b-field>
                    <b-field
                        label="Options excluded by dynamic filter"
                        horizontal>
                        <b-select v-model="table.row.referenceFilterDynamicShowFiltered">
                            <option :value="false">
                                Hide
                            </option>
                            <option :value="true">
                                Show, but unselectable
                            </option>
                        </b-select>
                    </b-field>
                    <b-field
                        label="Dynamic filter hint"
                        horizontal>
                        <b-input
                            v-model="table.row.referenceFilterDynamicErrorMessage"
                            maxlength="256"
                            placeholder="Warning shown when current value no longer matches filter" />
                    </b-field>
                    <b-field
                        v-if="isRelationCodeRef(table.row)"
                        label="Display in related library"
                        horizontal>
                        <b-checkbox
                            v-model="table.row.treatAsAttribute"
                            @update:model-value="(val: boolean) => { table.row.treatAsAttributeLabel = val ? (table.row.referenceLibraryName || null) : null; }" />
                    </b-field>
                    <b-field
                        v-if="isRelationCodeRef(table.row)"
                        label="Label in related library"
                        :type="(table.row.treatAsAttribute && !table.row.treatAsAttributeLabel) ? 'is-danger' : undefined"
                        :message="(table.row.treatAsAttribute && !table.row.treatAsAttributeLabel) ? 'Label is required' : undefined"
                        horizontal>
                        <b-input
                            v-model="table.row.treatAsAttributeLabel"
                            :disabled="!table.row.treatAsAttribute"
                            maxlength="256" />
                    </b-field>
                </div>
                <b-field
                    v-if="isStringType(table.row.attributeType)"
                    label="Regex"
                    :message="regexErrorMessage(table.row)"
                    :type="regexErrorMessage(table.row) ? 'is-danger' : ''"
                    horizontal
                    @input="regexErrors[table.row.name] = validateRegex(table.row.regex)">
                    <b-input v-model="table.row.regex" />
                </b-field>
                <b-field
                    v-if="isStringType(table.row.attributeType)"
                    label="Regex validation hint"
                    :type="isRegexErrorMessageWithoutRegex(table.row.regex, table.row.regexErrorMessage)
                        ? 'is-danger'
                        : undefined"
                    :message="isRegexErrorMessageWithoutRegex(table.row.regex, table.row.regexErrorMessage)
                        ? 'Hint is only valid when regex is set'
                        : undefined"
                    horizontal>
                    <b-input v-model="table.row.regexErrorMessage" />
                </b-field>
                <div
                    v-if="isStringType(table.row.attributeType)"
                    class="pt-4" />
                <b-field
                    v-if="isStringType(table.row.attributeType)"
                    label="Enable multiline"
                    horizontal>
                    <b-checkbox
                        :model-value="table.row.textAreaLines > 0"
                        @input="onClickEnableMultiline(table.row)" />
                </b-field>
                <b-field
                    v-if="isStringType(table.row.attributeType)"
                    label="Text area lines"
                    :type="table.row.textAreaLines < 0 ? 'is-danger' : undefined"
                    :message="table.row.textAreaLines < 0 ? 'Must be zero or positive' : undefined"
                    horizontal>
                    <b-input
                        v-model.number="table.row.textAreaLines"
                        type="number"
                        :disabled="table.row.textAreaLines === 0"
                        :min="table.row.textAreaLines > 0 ? 1 : 0"
                        class="w-6"
                        @update:model-value="(val: number) => { if ((val ?? 0) <= 0) table.row.isMarkdown = false; }" />
                </b-field>
                <b-field
                    v-if="isStringType(table.row.attributeType)"
                    label="Display markdown"
                    horizontal>
                    <b-checkbox
                        v-model="table.row.isMarkdown"
                        :disabled="table.row.textAreaLines === 0" />
                </b-field>
                <br>
                <b-field
                    horizontal
                    label="In identity">
                    <b-checkbox
                        v-model="table.row.includeInIdentity"
                        :disabled="isRelationLibrary"
                        :title="isRelationCodeRef(table.row) ? 'CodeRefs for related libraries must be in identity' : (isRelationLibrary ? 'Relation library can only have two CodeRef attributes in identity' : undefined)" />
                </b-field>
                <b-field
                    horizontal
                    label="Required">
                    <b-checkbox
                        v-model="table.row.required"
                        :disabled="isRelationLibrary && !isRelationCodeRef(table.row)"
                        :title="(isRelationLibrary && !isRelationCodeRef(table.row)) ? 'Only the two CodeRefs in identity can be required in relation libraries' : undefined" />
                </b-field>
            </template>
        </b-table>
        <add-choice-modal
            v-if="choiceModalTarget"
            :attribute-type="choiceModalTarget.attributeType"
            :regex="choiceModalTarget.regex"
            :regex-error-message="choiceModalTarget.regexErrorMessage"
            :existing-choices="choiceModalTarget.choices.map(c => c.value?.toString() ?? '')"
            @confirm="onChoiceConfirmed"
            @cancel="onChoiceCancelled" />
        <dynamic-filter-builder-modal
            v-if="openDynamicFilterAttribute"
            :reference-library-name="openDynamicFilterAttribute.referenceLibraryName ?? ''"
            :current-library-name="props.library.name"
            :current-library-attr-defs="dynamicFilterParamOptions"
            :existing-predicate="openDynamicFilterAttribute.referenceFilterDynamic ?? null"
            :library-case-sensitivity="libraryCaseSensitivity"
            @confirm="onDynamicFilterConfirmed"
            @clear="onDynamicFilterCleared"
            @cancel="onDynamicFilterCancelled" />
        <attribute-rename-modal
            v-if="renameModalTarget"
            :current-name="displayName(renameModalTarget)"
            :other-names="otherAttributeNames"
            @confirm="onRenameConfirmed"
            @cancel="onRenameCancelled" />
    </div>
</template>

<style scoped>
.not-allowed {
    cursor: not-allowed;
}

.pl-0-5 {
    padding-left: 0.5em;
}

.checkbox {
    padding-top: 0.2em;
}

.w-6 {
    width: 6em;
}

.w-20 {
    width: 20em;
}

.w-40 {
    width: 40em;
}
</style>
